import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [rbac, setRbac] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPrefs = useCallback(async () => {
    try {
      const { data } = await api.get("/preferences");
      setPrefs(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const loadRbac = useCallback(async () => {
    try {
      const { data } = await api.get("/rbac/me");
      setRbac(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then(async (r) => { setUser(r.data); await Promise.all([loadPrefs(), loadRbac()]); })
      .catch(() => localStorage.removeItem("token"))
      .finally(() => setLoading(false));
  }, [loadPrefs, loadRbac]);

  // Refresh permissions & profile when the app regains focus, so changes an owner makes
  // (e.g. granting "My Calendar") reach an employee without needing a full log out / log in.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible" && localStorage.getItem("token")) {
        loadRbac();
        api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => { document.removeEventListener("visibilitychange", refresh); window.removeEventListener("focus", refresh); };
  }, [loadRbac]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.token);
    setUser(data.user);
    await Promise.all([loadPrefs(), loadRbac()]);
    return data.user;
  };

  const loginWithSession = async (token, sessionUser) => {
    localStorage.setItem("token", token);
    setUser(sessionUser);
    await Promise.all([loadPrefs(), loadRbac()]);
    return sessionUser;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("token", data.token);
    setUser(data.user);
    await Promise.all([loadPrefs(), loadRbac()]);
    return data.user;
  };

  const savePrefs = async (patch) => {
    const { data } = await api.put("/preferences", patch);
    setPrefs(data);
    return data;
  };

  const refreshUser = useCallback(async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data); return data; } catch { return null; }
  }, []);

  const can = useCallback((key) => !!(rbac && rbac.permissions && rbac.permissions[key]), [rbac]);

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setPrefs(null);
    setRbac(null);
  };

  return (
    <AuthContext.Provider value={{ user, prefs, rbac, loading, login, loginWithSession, register, logout, loadPrefs, savePrefs, loadRbac, can, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
